import React, { useEffect, useState, useCallback, useRef } from 'react';
import { usePresence } from '../../hooks/usePresence';
import { stringToColor } from '../../utils/stringToColor';
import './PerfectCursors.css';

interface CursorData {
  userId: string;
  x: number;
  y: number;
  name: string;
  color: string;
  timestamp: number;
}

interface PerfectCursorsProps {
  roomType: 'project' | 'mindmap';
  roomId: string;
  containerRef?: React.RefObject<HTMLElement>;
}

const CURSOR_TIMEOUT = 5000; // Hide cursor after 5 seconds of inactivity
const SMOOTHING_FACTOR = 0.15; // For smooth cursor interpolation

export const PerfectCursors: React.FC<PerfectCursorsProps> = ({
  roomType,
  roomId,
  containerRef,
}) => {
  const [cursors, setCursors] = useState<Map<string, CursorData>>(new Map());
  const [smoothCursors, setSmoothCursors] = useState<Map<string, CursorData>>(new Map());
  const animationFrameRef = useRef<number>();
  const lastUpdateRef = useRef<Map<string, number>>(new Map());
  
  const { peers, publishCursor, user } = usePresence({
    roomType,
    roomId,
    enableCursor: true,
  });

  // Handle local mouse movement
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!publishCursor) return;
    
    let x = e.clientX;
    let y = e.clientY;
    
    // If we have a container, calculate relative position
    if (containerRef?.current) {
      const rect = containerRef.current.getBoundingClientRect();
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    // Throttle updates to ~60fps
    const now = Date.now();
    const lastUpdate = lastUpdateRef.current.get('local') || 0;
    if (now - lastUpdate < 16) return;
    
    lastUpdateRef.current.set('local', now);
    publishCursor({ x, y });
  }, [publishCursor, containerRef]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (!publishCursor) return;
    publishCursor(null); // Clear cursor when leaving
  }, [publishCursor]);

  // Set up mouse event listeners
  useEffect(() => {
    const target = containerRef?.current || document.body;
    
    target.addEventListener('mousemove', handleMouseMove as any);
    target.addEventListener('mouseleave', handleMouseLeave as any);
    
    return () => {
      target.removeEventListener('mousemove', handleMouseMove as any);
      target.removeEventListener('mouseleave', handleMouseLeave as any);
    };
  }, [handleMouseMove, handleMouseLeave, containerRef]);

  // Update cursors from peers
  useEffect(() => {
    if (!peers) return;
    
    const newCursors = new Map<string, CursorData>();
    
    Object.entries(peers).forEach(([peerId, peer]: [string, any]) => {
      if (peer.cursorPosition?.x !== undefined && peer.cursorPosition?.y !== undefined) {
        const name = peer.name || peer.email?.split('@')[0] || 'Anonymous';
        const color = peer.color || stringToColor(peerId, { saturation: 70, lightness: 50 });
        
        newCursors.set(peerId, {
          userId: peerId,
          x: peer.cursorPosition.x,
          y: peer.cursorPosition.y,
          name,
          color,
          timestamp: Date.now(),
        });
      }
    });
    
    setCursors(newCursors);
  }, [peers]);

  // Smooth cursor movement with interpolation
  useEffect(() => {
    const animate = () => {
      setSmoothCursors(prev => {
        const next = new Map(prev);
        const now = Date.now();
        
        // Update positions with interpolation
        cursors.forEach((cursor, userId) => {
          const existing = prev.get(userId);
          if (existing) {
            // Smooth interpolation
            const dx = cursor.x - existing.x;
            const dy = cursor.y - existing.y;
            
            next.set(userId, {
              ...cursor,
              x: existing.x + dx * SMOOTHING_FACTOR,
              y: existing.y + dy * SMOOTHING_FACTOR,
            });
          } else {
            // New cursor, add directly
            next.set(userId, cursor);
          }
        });
        
        // Remove stale cursors
        next.forEach((cursor, userId) => {
          if (now - cursor.timestamp > CURSOR_TIMEOUT && !cursors.has(userId)) {
            next.delete(userId);
          }
        });
        
        return next;
      });
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [cursors]);

  return (
    <div className="perfect-cursors-container">
      {Array.from(smoothCursors.entries()).map(([userId, cursor]) => (
        <div
          key={userId}
          className="perfect-cursor"
          style={{
            transform: `translate(${cursor.x}px, ${cursor.y}px)`,
            '--cursor-color': cursor.color,
          } as React.CSSProperties}
        >
          <svg
            className="perfect-cursor-svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19841L11.7841 12.3673H5.65376Z"
              fill={cursor.color}
              stroke="white"
              strokeWidth="1"
            />
          </svg>
          <div 
            className="perfect-cursor-label"
            style={{ backgroundColor: cursor.color }}
          >
            {cursor.name}
          </div>
        </div>
      ))}
    </div>
  );
};